import React, { useState, useEffect, useRef } from 'react';
import { Button, Form, Select, Space, Tag, Row, Col } from 'antd';
import { additionalAPI, DivValueOption } from '../../services/additional-api';
import { addOneHour, subtractOneHour } from '../../utils/timeUtils';
import ClearableTimeSelect from '../ClearableTimeSelect';
import './AppointmentTimePanel.css';

export interface OnsiteCustomer {
  customerno: number;
  customername: string;
  companyname?: string;
  deptname?: string;
}

export interface AppointmentContactUser {
  userno: number;
  username: string;
  deptname: string;
}

// ----------------------------------------------------------------
// AppointmentTimePanel
// 訪問/来社フィールド（予定時刻・アポイント・目的・開始終了時刻・客先同席者）の
// アニメーション展開エリア。isOnsite=true の間のみ表示される。
// ----------------------------------------------------------------

interface AppointmentTimePanelProps {
  isOnsite: boolean;
  scheduledTime: string;
  onScheduledTimeChange: (v: string) => void;
  appointment: string;
  onAppointmentChange: (v: string) => void;
  visitPurpose: string;
  onVisitPurposeChange: (v: string) => void;
  startTime: string;
  onStartTimeChange: (v: string) => void;
  endTime: string;
  onEndTimeChange: (v: string) => void;
  onsiteCustomers: OnsiteCustomer[];
  onOnsiteCustomerRemove: (customerno: number) => void;
  onOnsiteCustomersClear: () => void;
  onCustomerSelectOpen: () => void;
  /** 'new' の場合のみ API デフォルト値を自動セット。デフォルト 'new' */
  mode?: 'new' | 'edit';
}

export const AppointmentTimePanel: React.FC<AppointmentTimePanelProps> = ({
  isOnsite,
  scheduledTime,
  onScheduledTimeChange,
  appointment,
  onAppointmentChange,
  visitPurpose,
  onVisitPurposeChange,
  startTime,
  onStartTimeChange,
  endTime,
  onEndTimeChange,
  onsiteCustomers,
  onOnsiteCustomerRemove,
  onOnsiteCustomersClear,
  onCustomerSelectOpen,
  mode = 'new',
}) => {
  const [appointOptions, setAppointOptions] = useState<{ value: string; label: string }[]>([]);
  const [purposeOptions, setPurposeOptions] = useState<{ value: string; label: string }[]>([]);

  // Refs to read current prop values inside useEffect without adding them as deps
  const appointmentRef = useRef(appointment);
  appointmentRef.current = appointment;
  const visitPurposeRef = useRef(visitPurpose);
  visitPurposeRef.current = visitPurpose;
  const onAppointmentChangeRef = useRef(onAppointmentChange);
  onAppointmentChangeRef.current = onAppointmentChange;
  const onVisitPurposeChangeRef = useRef(onVisitPurposeChange);
  onVisitPurposeChangeRef.current = onVisitPurposeChange;
  const modeRef = useRef(mode);
  modeRef.current = mode;

  useEffect(() => {
    if (!isOnsite) return;

    // 生のアポイントリストから options 設定＋デフォルト適用
    // （キャッシュ表示・裏の再取得完了時のライブ反映のどちらからも呼ばれる）
    const applyAppoint = (list: DivValueOption[]) => {
      setAppointOptions(
        list.map((item) => ({ value: item.divvalue, label: item.divvaluename }))
      );
      const defaultAppoint = list.find((item) => item.defaultflag);
      if (defaultAppoint && !appointmentRef.current && modeRef.current === 'new') {
        onAppointmentChangeRef.current(defaultAppoint.divvalue);
      }
    };
    // 生の目的リストから options 設定＋デフォルト適用（同上）
    const applyPurpose = (list: DivValueOption[]) => {
      setPurposeOptions(
        list.map((item) => ({ value: item.divvalue, label: item.divvaluename }))
      );
      const defaultPurpose = list.find((item) => item.defaultflag);
      if (defaultPurpose && !visitPurposeRef.current && modeRef.current === 'new') {
        onVisitPurposeChangeRef.current(defaultPurpose.divvalue);
      }
    };

    // additionalAPI 側がキャッシュ確認・TTL判定・裏取得を持つ（他マスタと共通処理）。
    // 目的／アポイントだけ、裏取得完了時に onFresh 経由で画面へライブ反映する
    // （defaultflag 自動選択に関わるため。他4マスタは反映しない）。
    additionalAPI.getAppointList(applyAppoint).then((res) => {
      if (res.success && res.data) applyAppoint(res.data);
      else console.error('アポイント一覧取得エラー:', res.message);
    });
    additionalAPI.getPurposeList(applyPurpose).then((res) => {
      if (res.success && res.data) applyPurpose(res.data);
      else console.error('目的一覧取得エラー:', res.message);
    });
  }, [isOnsite]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateRows: isOnsite ? '1fr' : '0fr',
        opacity: isOnsite ? 1 : 0,
        transition: 'grid-template-rows 0.35s ease, opacity 0.3s ease',
        marginBottom: isOnsite ? 16 : 0,
      }}
    >
      <div
        style={{
          overflow: 'hidden',
          backgroundColor: '#e6f4ff',
          borderRadius: 8,
          padding: isOnsite ? '12px 12px 4px' : '0 12px',
        }}
      >
        {/* 予定時刻 */}
        <Form.Item style={{ marginBottom: 12 }}>
          <ClearableTimeSelect
            placeholder="予定時刻"
            value={scheduledTime || undefined}
            onChange={v => onScheduledTimeChange(v || '')}
            onClear={() => onScheduledTimeChange('')}
          />
        </Form.Item>

        {/* アポイント */}
        <Form.Item style={{ marginBottom: 12 }}>
          <Select
            value={appointment}
            onChange={v => onAppointmentChange(v ?? '')}
            style={{ width: '100%' }}
          >
            <Select.Option value="" style={{ color: '#bfbfbf' }}>アポイント</Select.Option>
            {appointOptions.map(opt => (
              <Select.Option key={opt.value} value={opt.value}>{opt.label}</Select.Option>
            ))}
          </Select>
        </Form.Item>

        {/* 目的 */}
        <Form.Item style={{ marginBottom: 12 }}>
          <Select
            value={visitPurpose}
            onChange={v => onVisitPurposeChange(v ?? '')}
            style={{ width: '100%' }}
          >
            <Select.Option value="" style={{ color: '#bfbfbf' }}>目的</Select.Option>
            {purposeOptions.map(opt => (
              <Select.Option key={opt.value} value={opt.value}>{opt.label}</Select.Option>
            ))}
          </Select>
        </Form.Item>

        {/* 開始〜終了時刻 */}
        <Form.Item style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
            <div className="appointment-time-col">
              <ClearableTimeSelect
                placeholder="開始時刻"
                value={startTime || undefined}
                onChange={v => {
                  onStartTimeChange(v || '');
                  if (v && !endTime) onEndTimeChange(addOneHour(v));
                }}
                onClear={() => onStartTimeChange('')}
              />
            </div>
            <div className="appointment-time-sep" style={{ textAlign: 'center' }}>
              <span>〜</span>
            </div>
            <div className="appointment-time-col">
              <ClearableTimeSelect
                placeholder="終了時刻"
                value={endTime || undefined}
                onChange={v => {
                  onEndTimeChange(v || '');
                  if (v && !startTime) onStartTimeChange(subtractOneHour(v));
                }}
                onClear={() => onEndTimeChange('')}
              />
            </div>
          </div>
        </Form.Item>

        {/* 同席者（客先） */}
        <Form.Item label="同席者（客先）" style={{ marginBottom: 8 }}>
          {onsiteCustomers.length > 0 && (
            <div style={{ marginBottom: 8 }}>
              {onsiteCustomers.map(customer => {
                const id = customer.customerno;
                return (
                  <Tag
                    key={id}
                    style={{ padding: '4px 8px', marginBottom: '6px', fontSize: '14px', display: 'inline-flex', alignItems: 'center' }}
                    closable
                    onClose={e => {
                      e.preventDefault();
                      onOnsiteCustomerRemove(id);
                    }}
                  >
                    {customer.customername}{customer.companyname ? `（${customer.companyname}）` : ''}
                  </Tag>
                );
              })}
            </div>
          )}
          <Space>
            <Button onClick={onCustomerSelectOpen}>同席者追加</Button>
            <Button danger onClick={onOnsiteCustomersClear}>同席者全削除</Button>
          </Space>
        </Form.Item>
      </div>
    </div>
  );
};

// ----------------------------------------------------------------
// AppointmentInternalAttendeesSection
// 社内同席者の選択エリア。isOnsite=true の間のみ表示される。
// ----------------------------------------------------------------

interface AppointmentInternalAttendeesSectionProps {
  isOnsite: boolean;
  selectedInternalAttendees: number[];
  onSelectedInternalAttendeesChange: (v: number[]) => void;
  contactUsers: AppointmentContactUser[];
  /** 社内同席者一覧から除外するユーザーのuserno（ログインユーザー除外などに使用） */
  excludeCurrentUserNo?: number;
}

export const AppointmentInternalAttendeesSection: React.FC<AppointmentInternalAttendeesSectionProps> = ({
  isOnsite,
  selectedInternalAttendees,
  onSelectedInternalAttendeesChange,
  contactUsers,
  excludeCurrentUserNo,
}) => {
  const [addingInternalAttendee, setAddingInternalAttendee] = useState(false);

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateRows: isOnsite ? '1fr' : '0fr',
        opacity: isOnsite ? 1 : 0,
        transition: 'grid-template-rows 0.35s ease, opacity 0.3s ease',
        marginTop: isOnsite ? 8 : 0,
        marginBottom: isOnsite ? 16 : 0,
      }}
    >
      <div
        style={{
          overflow: 'hidden',
          backgroundColor: '#e6f4ff',
          borderRadius: 8,
          padding: isOnsite ? '12px 12px 4px' : '0 12px',
        }}
      >
        <div style={{ marginBottom: 12 }}>
          <div style={{ marginBottom: 8 }}>
            <strong>同席者（社内）</strong>
          </div>
          {selectedInternalAttendees.length === 0 ? (
            <div style={{ minHeight: 32, display: 'flex', alignItems: 'center', marginBottom: 8 }}>
              <span style={{ color: 'rgba(0,0,0,0.45)' }}>社内同席者が設定されていません</span>
            </div>
          ) : (
            <div style={{ marginBottom: 8 }}>
              {selectedInternalAttendees.map(userno => {
                const u = contactUsers.find(cu => cu.userno === userno);
                return (
                  <Tag
                    key={userno}
                    style={{ padding: '4px 8px', marginBottom: '6px', fontSize: '14px', display: 'inline-flex', alignItems: 'center' }}
                    closable
                    onClose={e => {
                      e.preventDefault();
                      onSelectedInternalAttendeesChange(
                        selectedInternalAttendees.filter(id => id !== userno)
                      );
                    }}
                  >
                    {u ? u.username : (userno < 0 ? 'システム管理者' : '(削除済ユーザ)')}
                  </Tag>
                );
              })}
            </div>
          )}
          {!addingInternalAttendee ? (
            <Button
              type="dashed"
              size="small"
              onClick={() => setAddingInternalAttendee(true)}
              style={{ marginBottom: 8 }}
            >
              + 同席者を追加
            </Button>
          ) : (
            <div style={{ marginTop: 8, marginBottom: 8 }}>
              <Row gutter={8}>
                <Col span={16}>
                  <Select
                    style={{ width: '100%' }}
                    placeholder="追加する社内同席者を選択"
                    onChange={val => {
                      if (val && !selectedInternalAttendees.includes(Number(val))) {
                        onSelectedInternalAttendeesChange([...selectedInternalAttendees, Number(val)]);
                      }
                      setAddingInternalAttendee(false);
                    }}
                    onBlur={() => setAddingInternalAttendee(false)}
                  >
                    {contactUsers
                      .filter(u =>
                        !selectedInternalAttendees.includes(u.userno) &&
                        u.userno !== excludeCurrentUserNo
                      )
                      .map(u => (
                        <Select.Option key={u.userno} value={u.userno}>
                          {u.username} ({u.deptname})
                        </Select.Option>
                      ))}
                  </Select>
                </Col>
                <Col span={8}>
                  <Button
                    onClick={() => setAddingInternalAttendee(false)}
                    size="middle"
                    style={{ width: '100%', padding: '0 8px' }}
                  >
                    キャンセル
                  </Button>
                </Col>
              </Row>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
